import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import type { CaseDataField } from '@protocols/domain';

interface StagedCaseDataStateModel {
  /*
   * The ordered list of case-data fields defined for the case type being
   * edited. Wholly client-side until the save cascade persists it — there is no
   * persisted/override split (unlike pipelines/resources) because the whole
   * field set is owned by the case type and saved as one unit.
   */
  fields: CaseDataField[];

  /*
   * Internal names of the fields that belong to a persisted case type. A field
   * whose `name` is in this set has its internal name frozen in the editor
   * dialog: renaming its display name must not change the identifier that
   * existing cases reference. Seeded ONLY when an existing case type loads (see
   * `setLockedNames`); a create session leaves it empty, so every field's name
   * tracks its display name until the first save.
   */
  lockedNames: string[];

  /** True while the save cascade persists the staged fields. */
  saving: boolean;

  /** Last save failure, set by the cascade on its owner state (i18n mapping is the facade's job). */
  saveException: Error | null;
}

const INITIAL_STATE: StagedCaseDataStateModel = {
  fields: [],
  lockedNames: [],
  saving: false,
  saveException: null,
};

/**
 * Simple staging store for the case type's data fields — the `CaseDataManager`
 * writes into it as the user adds/edits/removes/reorders fields, and the
 * case-type editor reads it at consolidation time.
 *
 * A pure `ComponentStore` with NO companion Effects (matches the
 * `StagedPipelinesState`/`StagedResourcesState` precedent — those ledgers have
 * no effects; the cascade is their effect layer). Provided ONCE by the
 * outermost consumer (the case-type editor page) so the staged fields survive
 * the manager unmount and the field-editor dialog stack.
 */
@Injectable()
export class StagedCaseDataState extends ComponentStore<StagedCaseDataStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly fields$ = this.select((state) => state.fields);
  public readonly lockedNames$ = this.select((state) => state.lockedNames);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly saveException$ = this.select((state) => state.saveException);

  // ============================================================================
  // Updaters
  // ============================================================================

  /** Seed the field list (e.g. from a persisted case type on load). */
  public readonly setFields = this.updater((state, fields: CaseDataField[]) => ({ ...state, fields: [...fields] }));

  /**
   * Seed the locked-name ledger — the internal names whose fields are already
   * persisted and must not be renamed. Called only on the existing-case-type
   * load path; a create session never locks anything.
   */
  public readonly setLockedNames = this.updater((state, lockedNames: string[]) => ({
    ...state,
    lockedNames: [...lockedNames],
  }));

  /** Append a newly created field. */
  public readonly addField = this.updater((state, field: CaseDataField) => ({
    ...state,
    fields: [...state.fields, field],
  }));

  /** Replace the field at `index` with the edited value. No-op on an out-of-range index. */
  public readonly updateField = this.updater((state, payload: { index: number; field: CaseDataField }) => {
    if (payload.index < 0 || payload.index >= state.fields.length) return state;
    const fields = [...state.fields];
    fields[payload.index] = payload.field;
    return { ...state, fields };
  });

  /**
   * Remove the field at `index`. No-op on an out-of-range index. Also prunes the
   * removed field's name from the locked-name ledger, so a later new field that
   * happens to derive the same internal name is not wrongly frozen.
   */
  public readonly removeField = this.updater((state, index: number) => {
    if (index < 0 || index >= state.fields.length) return state;
    const removed = state.fields[index];
    return {
      ...state,
      fields: state.fields.filter((_, i) => i !== index),
      lockedNames: state.lockedNames.filter((name) => name !== removed.name),
    };
  });

  /** Replace the whole list with a reordered copy (drag-drop result). */
  public readonly reorder = this.updater((state, fields: CaseDataField[]) => ({ ...state, fields: [...fields] }));

  public readonly clear = this.updater(() => ({ ...INITIAL_STATE }));

  // ============================================================================
  // Cascade bookkeeping
  // ============================================================================

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));
  public readonly setSaveException = this.updater((state, saveException: Error | null) => ({
    ...state,
    saveException,
  }));
}
